import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, FormControl, Autocomplete, CircularProgress, Chip, Button,
  ToggleButtonGroup, ToggleButton, IconButton, InputLabel, Select, MenuItem,
  Card, CardMedia, CardContent, LinearProgress
} from '@mui/material';
import {
  FormatBold, FormatItalic, FormatStrikethrough, FormatUnderlined,
  Code, DataObject, Link as LinkIcon, VisibilityOff,
  Visibility, Edit, Delete, Image, VideoFile, AudioFile, Description,
  AttachFile, ErrorOutline
} from '@mui/icons-material';
import { API_BASE_URL } from 'envConfig';
import { apiClient } from '../../../utils/ApiClient';
import { Logger } from '../../../utils/Logger';

type MediaAttachment = {
  type: 'image' | 'video' | 'document' | 'audio';
  filename: string;
  mimeType: string;
  size: number;
  data?: string;
  localUrl?: string;
  isOrphaned?: boolean;
  isDeleted?: boolean;
  isNew?: boolean;
  originalName?: string;
};

type Props = {
  currentTemplate: any;
  setCurrentTemplate: (fn: any) => void;
  dataSets: any[];
  templateNameError: string;
  setTemplateNameError: (e: string) => void;
  companyId: string;
  getDisplayCollectionName: (name?: string) => string;
  onSaveError?: (error: string) => void;
  isEditing?: boolean;
};

const TELEGRAM_ACCENT = '#0088cc';
const TELEGRAM_ACCENT_DARK = '#006daa';

type TelegramFormat = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'monospace' | 'codeblock' | 'spoiler' | 'link';

const TELEGRAM_FORMAT_MAP: Record<TelegramFormat, { prefix: string; suffix: string }> = {
  bold:          { prefix: '*',   suffix: '*' },
  italic:        { prefix: '_',   suffix: '_' },
  underline:     { prefix: '__',  suffix: '__' },
  strikethrough: { prefix: '~',   suffix: '~' },
  monospace:     { prefix: '`',   suffix: '`' },
  codeblock:     { prefix: '```\n', suffix: '\n```' },
  spoiler:       { prefix: '||',  suffix: '||' },
  link:          { prefix: '[',   suffix: '](url)' },
};

const TEMPLATE_CATEGORIES = [
  { value: 'transactional', label: 'Transactional' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'service', label: 'Service' },
  { value: 'system', label: 'System' },
];

const formatTelegramMessage = (text: string) =>
  (text || '')
    .replace(/```([\s\S]*?)```/g, '<pre style="background:rgba(0,136,204,0.1);padding:8px 12px;border-radius:4px;font-family:monospace;white-space:pre-wrap;margin:4px 0">$1</pre>')
    .replace(/\|\|([^|]+)\|\|/g, '<span style="background:currentColor;border-radius:2px;cursor:pointer" title="Spoiler (hover to reveal)">$1</span>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#0088cc;text-decoration:underline">$1</a>')
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<u>$1</u>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<del>$1</del>')
    .replace(/`([^`]+)`/g, '<code style="font-family:monospace;background:rgba(0,136,204,0.1);padding:2px 4px;border-radius:3px">$1</code>')
    .replace(/\n/g, '<br>');

export const TelegramTemplate: React.FC<Props> = ({
  currentTemplate,
  setCurrentTemplate,
  dataSets,
  templateNameError,
  setTemplateNameError,
  companyId,
  getDisplayCollectionName,
  onSaveError: _onSaveError,
  isEditing = false
}) => {
  const nameRegex = /^[a-zA-Z0-9_-]*$/;

  const [textValue, setTextValue] = useState<string>(String(currentTemplate?.templateContent || ''));
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const textFieldRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [lastFetchedDataSet, setLastFetchedDataSet] = useState<string>('');
  const [mediaAttachments, setMediaAttachments] = useState<MediaAttachment[]>([]);
  const [mediaLoadErrors, setMediaLoadErrors] = useState<Set<string>>(new Set());
  const [uploadingMedia, setUploadingMedia] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [mediaBlobUrls, setMediaBlobUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setTextValue(String(currentTemplate?.templateContent || ''));
  }, [currentTemplate?.templateContent]);

  const getDisplayTemplateName = (fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    const parts = fullTemplateName.split('-');
    if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === companyId) {
      return parts.slice(2).join('-');
    }
    return fullTemplateName;
  };

  const fetchDataSetColumns = async (dataSetId: string) => {
    if (!dataSetId) { setAvailableColumns([]); setLastFetchedDataSet(''); return; }
    if (lastFetchedDataSet === dataSetId) return;
    try {
      setLoadingColumns(true);
      const selected = dataSets.find((ds: any) => ds._id === dataSetId);
      if (!selected) return;
      const full = selected.collectionName;
      const parts = full.split('-');
      const base = parts.length >= 3 ? parts.slice(2).join('-') : full;
      const resp = await fetch(`${API_BASE_URL}/iu/ds/cmp/set/entry/schema/${encodeURIComponent(base)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('tokenid')}` }
      });
      const json = await resp.json();
      const schema = json.data || json;
      const cols = Object.keys(schema.paths || {}).filter((k: string) => !['_id', 'createdAt', 'updatedAt', '__v'].includes(k));
      setAvailableColumns(cols);
      setLastFetchedDataSet(dataSetId);
    } catch {
      setAvailableColumns([]);
      setLastFetchedDataSet('');
    } finally {
      setLoadingColumns(false);
    }
  };

  useEffect(() => {
    if (currentTemplate?.collectionId) fetchDataSetColumns(currentTemplate.collectionId);
    else setAvailableColumns([]);
  }, [currentTemplate?.collectionId, dataSets]);

  const applyFormatting = (format: TelegramFormat) => {
    const { prefix, suffix } = TELEGRAM_FORMAT_MAP[format];
    const el = textFieldRef.current;
    const start = el ? el.selectionStart : (selection?.start ?? textValue.length);
    const end = el ? el.selectionEnd : (selection?.end ?? textValue.length);
    const hasSelection = start !== end;
    const before = textValue.substring(0, start);
    const selected = hasSelection ? textValue.substring(start, end) : '';
    const after = textValue.substring(end);

    let newText: string;
    let cursorPos: number;
    if (hasSelection) {
      newText = before + prefix + selected + suffix + after;
      cursorPos = start + prefix.length + selected.length + suffix.length;
    } else {
      const placeholder = format === 'link' ? 'text' : format === 'codeblock' ? 'code' : 'text';
      newText = before + prefix + placeholder + suffix + after;
      cursorPos = start + prefix.length;
    }
    setTextValue(newText);
    setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);

    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        if (!hasSelection) {
          const pLen = format === 'link' ? 4 : format === 'codeblock' ? 4 : 4;
          el.setSelectionRange(cursorPos, cursorPos + pLen);
        } else {
          el.setSelectionRange(cursorPos, cursorPos);
        }
      }
    });
  };

  useMemo(() => {
    if (isEditing && currentTemplate?._id && currentTemplate?.mediaAttachments && currentTemplate.mediaAttachments.length > 0) {
      const validAttachments = currentTemplate.mediaAttachments.filter((att: any) => att && att.filename && att.mimeType && att.type);
      const attachmentsWithoutData = validAttachments.map((att: any) => ({ ...att, data: undefined, localUrl: undefined }));
      setMediaAttachments(attachmentsWithoutData);
    } else if (isEditing && currentTemplate?._id && (!currentTemplate?.mediaAttachments || currentTemplate.mediaAttachments.length === 0)) {
      setMediaAttachments([]);
    }
  }, [isEditing, currentTemplate?._id]);

  useEffect(() => {
    return () => { mediaBlobUrls.forEach(url => URL.revokeObjectURL(url)); setMediaBlobUrls(new Map()); };
  }, []);

  useEffect(() => {
    return () => { mediaBlobUrls.forEach(url => URL.revokeObjectURL(url)); };
  }, [mediaAttachments]);

  const fetchMediaAsBlob = async (attachment: MediaAttachment) => {
    try {
      const token = localStorage.getItem('tokenid');
      const response = await fetch(`${API_BASE_URL}/iu/template/media/${currentTemplate?._id}/${attachment.filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setMediaBlobUrls(prev => new Map(prev).set(attachment.filename, blobUrl));
      } else {
        setMediaLoadErrors(prev => new Set(prev).add(attachment.filename));
      }
    } catch {
      setMediaLoadErrors(prev => new Set(prev).add(attachment.filename));
    }
  };

  useEffect(() => {
    if (isEditing && currentTemplate?._id && mediaAttachments.length > 0) {
      mediaAttachments.forEach(att => {
        if (!att.isDeleted && !att.localUrl && !att.data && !att.isOrphaned) {
          fetchMediaAsBlob(att);
        }
      });
    }
  }, [isEditing, currentTemplate?._id, mediaAttachments]);

  const handleMediaUpload = async (file: File) => {
    try {
      setUploadingMedia(true);
      setUploadError('');
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 20MB limit.`);
      }
      const formData = new FormData();
      formData.append('media', file);
      formData.append('companyId', companyId);
      if (currentTemplate?._id) formData.append('templateId', currentTemplate._id);

      const token = localStorage.getItem('tokenid');
      const response = await fetch(`${API_BASE_URL}/iu/template/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }
      const result = await response.json();
      const responseData = result.data || result;
      const localUrl = URL.createObjectURL(file);
      const newAttachment: MediaAttachment = {
        type: responseData.type,
        filename: responseData.filename,
        originalName: file.name,
        mimeType: responseData.mimeType,
        size: responseData.size,
        data: responseData.data,
        isNew: true,
        localUrl
      };
      const updated = [...mediaAttachments, newAttachment];
      setMediaAttachments(updated);
      setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: updated } : null);
    } catch (error) {
      Logger.error('TelegramTemplate', `Media upload failed: ${error}`);
      setUploadError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleMediaDelete = (filename: string) => {
    const updated = mediaAttachments.map(att => att.filename === filename ? { ...att, isDeleted: true } : att);
    setMediaAttachments(updated);
    setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: updated } : null);
  };

  const getMediaUrl = (attachment: MediaAttachment): string => {
    if (attachment.localUrl) return attachment.localUrl;
    const blobUrl = mediaBlobUrls.get(attachment.filename);
    if (blobUrl) return blobUrl;
    if (attachment.data) {
      if (attachment.data.startsWith('data:')) return attachment.data;
      return `data:${attachment.mimeType};base64,${attachment.data}`;
    }
    return '';
  };

  const activeAttachments = mediaAttachments.filter(a => a && a.mimeType && !a.isDeleted);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField
          size="small" label="Template Name" fullWidth
          value={getDisplayTemplateName(currentTemplate?.templateName)}
          onChange={e => {
            const v = e.target.value;
            if (!nameRegex.test(v)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
            else setTemplateNameError('');
            setCurrentTemplate((prev: any) => prev ? { ...prev, templateName: v } : null);
          }}
          error={!!templateNameError} helperText={templateNameError}
        />
        <Autocomplete
          size="small" fullWidth
          options={dataSets}
          getOptionLabel={(option: any) => getDisplayCollectionName(option.collectionName)}
          value={dataSets.find((ds: any) => ds._id === currentTemplate?.collectionId) || null}
          onChange={(_e, newValue: any) => setCurrentTemplate((prev: any) => prev ? { ...prev, collectionId: newValue?._id || '' } : null)}
          renderInput={(params) => <TextField {...params} label="Select DataSet" InputProps={{ ...params.InputProps, endAdornment: <>{loadingColumns ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
          isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
            onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, accessLevel: e.target.value } : null)}>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
        <TextField
          select size="small" label="Category" fullWidth
          value={currentTemplate?.category || ''}
          onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, category: e.target.value } : null)}
          SelectProps={{ displayEmpty: true, renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === (v as string))?.label ?? String(v)) : 'Select category' }}
          InputLabelProps={{ shrink: true }}
        >
          {TEMPLATE_CATEGORIES.map(cat => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
        </TextField>
        <Autocomplete
          multiple freeSolo size="small" options={[]}
          value={currentTemplate?.keywords || []}
          onChange={(_e, newValue) => setCurrentTemplate((prev: any) => prev ? { ...prev, keywords: newValue as string[] } : null)}
          renderTags={(value, getTagProps) => value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={index} />)}
          renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
        />
      </Box>

      {/* Merge fields from DataSet columns */}
      {availableColumns.length > 0 && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0,136,204,0.1)' : 'rgba(0,136,204,0.05)', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            Available merge fields (click to insert):
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {availableColumns.map(col => (
              <Chip key={col} label={`{{${col}}}`} size="small" variant="outlined"
                onClick={() => {
                  const el = textFieldRef.current;
                  const pos = el ? el.selectionStart : textValue.length;
                  const tag = `{{${col}}}`;
                  const newText = textValue.substring(0, pos) + tag + textValue.substring(pos);
                  setTextValue(newText);
                  setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);
                  requestAnimationFrame(() => { if (el) { el.focus(); const newPos = pos + tag.length; el.setSelectionRange(newPos, newPos); } });
                }}
                sx={{ cursor: 'pointer', '&:hover': { borderColor: TELEGRAM_ACCENT, color: TELEGRAM_ACCENT } }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Content area */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Template Content *</Typography>
          <Button size="small" variant={showPreview ? "contained" : "outlined"}
            startIcon={showPreview ? <Edit /> : <Visibility />}
            onClick={() => setShowPreview(!showPreview)}
            sx={{
              flexShrink: 0,
              backgroundColor: showPreview ? TELEGRAM_ACCENT : 'transparent',
              color: showPreview ? '#ffffff' : TELEGRAM_ACCENT,
              borderColor: TELEGRAM_ACCENT,
              '&:hover': { backgroundColor: showPreview ? TELEGRAM_ACCENT_DARK : 'rgba(0, 136, 204, 0.1)' }
            }}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        </Box>
        {showPreview ? (
          <Box sx={{
            border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
            borderRadius: '8px', padding: '16px',
            background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f0f2f5',
            minHeight: '200px'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Telegram Message Preview</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box sx={{
                maxWidth: '80%', background: theme => theme.palette.mode === 'dark' ? '#1e3a5f' : '#e3f2fd',
                color: theme => theme.palette.text.primary,
                padding: '8px 12px', borderRadius: '4px 18px 18px 18px',
                wordWrap: 'break-word',
                '& strong': { fontWeight: 'bold' }, '& em': { fontStyle: 'italic' },
                '& u': { textDecoration: 'underline' }, '& del': { textDecoration: 'line-through' },
                '& pre': { whiteSpace: 'pre-wrap' },
              }}
              dangerouslySetInnerHTML={{ __html: formatTelegramMessage(textValue) }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{
            border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
            borderRadius: '8px', overflow: 'hidden', width: '100%'
          }}>
            <Box sx={{
              background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
              borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
              padding: '6px 12px', display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center'
            }}>
              <ToggleButtonGroup size="small" sx={{ '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 } }}>
                <ToggleButton value="bold" onClick={() => applyFormatting('bold')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><FormatBold fontSize="small" /></ToggleButton>
                <ToggleButton value="italic" onClick={() => applyFormatting('italic')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><FormatItalic fontSize="small" /></ToggleButton>
                <ToggleButton value="underline" onClick={() => applyFormatting('underline')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><FormatUnderlined fontSize="small" /></ToggleButton>
                <ToggleButton value="strikethrough" onClick={() => applyFormatting('strikethrough')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><FormatStrikethrough fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ width: '1px', height: 20, backgroundColor: 'divider', mx: 0.5 }} />
              <ToggleButtonGroup size="small" sx={{ '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 } }}>
                <ToggleButton value="monospace" onClick={() => applyFormatting('monospace')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><Code fontSize="small" /></ToggleButton>
                <ToggleButton value="codeblock" onClick={() => applyFormatting('codeblock')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><DataObject fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ width: '1px', height: 20, backgroundColor: 'divider', mx: 0.5 }} />
              <ToggleButtonGroup size="small" sx={{ '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 } }}>
                <ToggleButton value="spoiler" onClick={() => applyFormatting('spoiler')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><VisibilityOff fontSize="small" /></ToggleButton>
                <ToggleButton value="link" onClick={() => applyFormatting('link')} sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}><LinkIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              multiline minRows={6} maxRows={10}
              value={textValue}
              onChange={e => { setTextValue(e.target.value); setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: e.target.value } : null); }}
              onSelect={e => { const t = e.target as HTMLTextAreaElement; setSelection({ start: t.selectionStart, end: t.selectionEnd }); }}
              inputRef={textFieldRef}
              variant="standard" fullWidth
              InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.4' } }}
              sx={{ '& .MuiInputBase-root': { minHeight: '150px', color: theme => theme.palette.text.primary, backgroundColor: theme => theme.palette.background.paper }, width: '100%' }}
            />
          </Box>
        )}
        <Box sx={{
          mt: 2, p: 1.5, borderRadius: 1,
          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 136, 204, 0.15)' : 'rgba(0, 136, 204, 0.08)',
          border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 136, 204, 0.3)' : 'rgba(0, 136, 204, 0.2)'}`,
        }}>
          <Typography variant="caption" color="text.secondary" component="div">
            <strong>Formatting:</strong> *bold* &nbsp; _italic_ &nbsp; __underline__ &nbsp; ~strikethrough~ &nbsp; `monospace` &nbsp; {'```code block```'} &nbsp; ||spoiler|| &nbsp; [text](url)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            <strong>Variables:</strong> Use double curly braces: {'{{columnName}}'} to insert merge fields from the selected DataSet.
          </Typography>
        </Box>
      </Box>

      {/* Media Attachments Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Media Attachments</Typography>
        <Box sx={{ mb: 2 }}>
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar"
            onChange={e => { const file = e.target.files?.[0]; if (file) { setUploadError(''); handleMediaUpload(file); } e.target.value = ''; }}
            style={{ display: 'none' }}
            id="telegram-template-media-upload"
            disabled={uploadingMedia}
          />
          <label htmlFor="telegram-template-media-upload">
            <Button variant="outlined" component="span"
              startIcon={uploadingMedia ? <CircularProgress size={16} /> : <AttachFile />}
              disabled={uploadingMedia}
              sx={{ borderColor: TELEGRAM_ACCENT, color: TELEGRAM_ACCENT, '&:hover': { borderColor: TELEGRAM_ACCENT_DARK, backgroundColor: 'rgba(0, 136, 204, 0.1)' } }}>
              {uploadingMedia ? 'Uploading...' : 'Add Media'}
            </Button>
          </label>
          {uploadingMedia && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
          {uploadError && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="error" sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                backgroundColor: 'rgba(244, 67, 54, 0.1)', padding: 1, borderRadius: 1,
                border: '1px solid rgba(244, 67, 54, 0.3)'
              }}>
                <ErrorOutline sx={{ fontSize: 16 }} />{uploadError}
              </Typography>
            </Box>
          )}
        </Box>
        {activeAttachments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activeAttachments.map((attachment, index) => (
              <Card key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden' }}>
                {attachment.mimeType?.startsWith('image/') && (
                  mediaLoadErrors.has(attachment.filename) ? (
                    <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover' }}>
                      <Box sx={{ textAlign: 'center' }}><Image sx={{ color: 'text.secondary', fontSize: 32, mb: 1 }} /><Typography variant="caption" color="text.secondary">Failed to load</Typography></Box>
                    </Box>
                  ) : (
                    <CardMedia component="img" height="120" image={getMediaUrl(attachment)} alt={attachment.originalName || attachment.filename} sx={{ objectFit: 'cover' }}
                      onError={() => setMediaLoadErrors(prev => new Set(prev).add(attachment.filename))} />
                  )
                )}
                {attachment.mimeType?.startsWith('video/') && (
                  <Box sx={{ position: 'relative', height: 120 }}>
                    <video src={getMediaUrl(attachment)} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setMediaLoadErrors(prev => new Set(prev).add(attachment.filename))} />
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 40, height: 40, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid white', ml: '2px' }} />
                    </Box>
                  </Box>
                )}
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {attachment.mimeType?.startsWith('image/') && <Image sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                    {attachment.mimeType?.startsWith('video/') && <VideoFile sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                    {attachment.mimeType?.startsWith('audio/') && <AudioFile sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                    {!attachment.mimeType?.startsWith('image/') && !attachment.mimeType?.startsWith('video/') && !attachment.mimeType?.startsWith('audio/') && <Description sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attachment.originalName || attachment.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attachment.mimeType?.split('/')[0]?.charAt(0).toUpperCase()}{attachment.mimeType?.split('/')[0]?.slice(1)} &bull; {((attachment.size || 0) / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleMediaDelete(attachment.filename)} sx={{ color: 'error.main' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TelegramTemplate;
